import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { api, apiPost } from '../lib/api';
import Logo from '../components/Logo';
import { FiCheck, FiArrowLeft, FiBriefcase, FiUsers } from 'react-icons/fi';
import { notify } from '../lib/notify';

export default function AddRole() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Detect role from URL path or query param
  const getRoleToAdd = () => {
    const path = location.pathname;
    if (path.includes('/register/creative') || path.includes('/creative')) return 'creative';
    if (path.includes('/register/client') || path.includes('/client')) return 'client';
    return searchParams.get('role') || 'creative';
  };
  const roleToAdd = getRoleToAdd();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    try {
      const token = localStorage.getItem('auth:token');
      const userStr = localStorage.getItem('auth:user');
      if (!token || !userStr) {
        navigate('/login?redirect=' + encodeURIComponent('/add-role?role=' + roleToAdd));
        return;
      }
      const userData = JSON.parse(userStr);
      setUser(userData);

      // Check if user already has this role
      const roles = Array.isArray(userData.roles) ? userData.roles : (userData.role ? [userData.role] : []);
      const normalizedRoles = roles.map(r => String(r).toLowerCase());
      if (normalizedRoles.includes(roleToAdd)) {
        notify.info(`You already have the ${roleToAdd} role!`);
        navigate('/dashboard');
      }
    } catch (e) {
      navigate('/login');
    }
  }, [navigate, roleToAdd]);

  const handleAddRole = async () => {
    setLoading(true);
    setError('');
    try {
      // Call API to add role
      const res = await apiPost('/users/add-role', { role: roleToAdd });
      
      if (res?.error) {
        throw new Error(res.error);
      }

      // Update local storage with new roles
      const updatedUser = res?.data?.user || res?.user;
      if (updatedUser) {
        localStorage.setItem('auth:user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

      setSuccess(true);
      notify.success(`You are now registered as a ${roleToAdd}!`);

      // Redirect after a short delay
      setTimeout(() => {
        navigate(roleToAdd === 'creative' ? '/dashboard/creative' : '/dashboard/client');
      }, 2000);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to add role';
      setError(msg);
      notify.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isCreative = roleToAdd === 'creative';
  const Icon = isCreative ? FiBriefcase : FiUsers;
  const title = isCreative ? 'Become a Creative' : 'Register as Client';
  const description = isCreative 
    ? 'Start offering your creative services to clients across the Nordics'
    : 'Book services from talented creatives and manage your projects';
  const benefits = isCreative 
    ? [
        'Create and sell your services',
        'Get discovered by clients',
        'Manage bookings and earnings',
        'Build your portfolio',
        'Set your own rates',
      ]
    : [
        'Book creative services',
        'Discover talented creatives',
        'Manage your bookings',
        'Secure escrow payments',
        'Rate and review services',
      ];

  return (
    <div className="min-h-screen bg-white dark:bg-black flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-secondary-text hover:text-primary-text mb-6"
        >
          <FiArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="card p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo className="h-12 w-auto" />
          </div>

          {success ? (
            // Success State
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-primary-text mb-2">Welcome!</h1>
              <p className="text-secondary-text mb-4">
                You are now registered as a {roleToAdd}. Redirecting to your dashboard...
              </p>
            </div>
          ) : (
            // Form State
            <>
              {/* Header */}
              <div className="text-center mb-6">
                <div className={`w-16 h-16 rounded-full ${isCreative ? 'bg-primary/10' : 'bg-blue-500/10'} flex items-center justify-center mx-auto mb-4`}>
                  <Icon className={`w-8 h-8 ${isCreative ? 'text-primary' : 'text-blue-500'}`} />
                </div>
                <h1 className="text-2xl font-bold text-primary-text">{title}</h1>
                <p className="text-secondary-text mt-2">{description}</p>
              </div>

              {/* Benefits */}
              <div className="bg-subtle-bg rounded-lg p-4 mb-6">
                <h3 className="font-medium text-primary-text mb-3">What you'll get:</h3>
                <ul className="space-y-2">
                  {benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-text">
                      <FiCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* User Info */}
              {user && (
                <div className="mb-6 p-3 rounded-lg bg-subtle-bg text-sm">
                  <span className="text-secondary-text">Adding role to: </span>
                  <span className="font-medium text-primary-text">{user.email}</span>
                </div>
              )}

              {/* Action Button */}
              <button
                onClick={handleAddRole}
                disabled={loading}
                className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  isCreative 
                    ? 'bg-primary text-white hover:bg-primary/90' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Processing...' : `Continue as ${isCreative ? 'Creative' : 'Client'}`}
              </button>

              {/* Terms */}
              <p className="text-xs text-center text-secondary-text mt-4">
                By continuing, you agree to our{' '}
                <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
